import { withAuthenticationRequired } from '@tom-ui/utils'
import { Box } from '@mui/material'
import { CargoType, CarrierType, CarrierVisitDirection, OrderResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { useGeneralCargoLocations } from '@planning/hooks/useGeneralCargoLocations'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { GeneralCargoOrderLocationItem } from '../GeneralCargo/Components/GeneralCargoOrderLocationItem'
import { BaplieParseResult } from '../Order/stores/OrderListUploadParsingStore'
import { VisitGeneralCargoTable } from '../Visits/Components/organisms/VisitGeneralCargoTable'
import { ContainerYardOperationViewStore } from './Components'
import { VesselVisitOrderListHeader } from './Components/molecules/VesselVisitOrderListHeader'
import { VesselVisitContainerDischargeTable } from './Components/organisms/VesselVisitTable/VesselVisitContainerDischargeTable'
import { OrderListUploadDialogV2 } from './Components/Upload/OrderListUploadDialog'
import { VesselVisitDischargeListViewStore } from './Stores/VesselVisitOrderListViewStore'

type Props = {
  vesselVisitItem: IVesselVisitItem
  containerYardOperationViewStore: ContainerYardOperationViewStore
  parseBaplie?: (file: File) => Promise<BaplieParseResult>
  onOpenGeneralCargoReservation?: (order: OrderResponseDto) => void
  getOrderLocationsFunc?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; locations: GeneralCargoOrderLocationItem[] }>>
  getOrderReservationsFunc?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; planned: GeneralCargoOrderLocationItem[] }>>
  reservationUpdate?: { orderId: number; reservations: GeneralCargoOrderLocationItem[] }
}

export const VesselVisitDischargeListPage: FC<Props> = withAuthenticationRequired(
  observer(
    ({
      containerYardOperationViewStore,
      vesselVisitItem,
      parseBaplie,
      onOpenGeneralCargoReservation,
      getOrderLocationsFunc,
      getOrderReservationsFunc,
      reservationUpdate,
    }) => {
      const theme = useMinimalsTheme()

      const { orderListUploadViewStoreV2 } = usePlanningStore()

      const vesselVisitDischargeListViewStore = useMemo(() => {
        return new VesselVisitDischargeListViewStore(vesselVisitItem)
      }, [vesselVisitItem])

      const onUploadClick = async () => {
        const visitId = vesselVisitItem?.id

        orderListUploadViewStoreV2.reset()

        await orderListUploadViewStoreV2.setVesselVisitId(visitId)
        await orderListUploadViewStoreV2.setDirection(CarrierVisitDirection.Inbound)

        orderListUploadViewStoreV2.setOpen(true)
      }

      const orders = useMemo(() => {
        return (
          vesselVisitDischargeListViewStore.vesselVisitItem?.discharge?.generalCargoOrders.map(
            order => order.data,
          ) ?? []
        )
      }, [vesselVisitDischargeListViewStore.vesselVisitItem?.discharge?.generalCargoOrders])

      const { orderLocationsById, orderReservationsById } = useGeneralCargoLocations(
        orders,
        getOrderLocationsFunc,
        getOrderReservationsFunc,
        reservationUpdate,
      )

      return (
        <>
          {vesselVisitItem && (
            <VesselVisitOrderListHeader
              onUploadClick={onUploadClick}
              vesselVisitItem={vesselVisitItem}
              direction='discharge'
            />
          )}
          <Box padding={theme.customSpacing.m}>
            {vesselVisitItem?.data.cargoType === CargoType.Container && (
              <VesselVisitContainerDischargeTable
                vesselVisitDischargeListViewStore={vesselVisitDischargeListViewStore}
                containerYardOperationViewStore={containerYardOperationViewStore}
              />
            )}

            {vesselVisitItem?.data.cargoType === CargoType.GeneralCargo && (
              <VisitGeneralCargoTable
                orders={
                  vesselVisitDischargeListViewStore.vesselVisitItem?.discharge?.generalCargoOrders
                }
                carrierVisit={vesselVisitItem}
                carrierVisitType={CarrierType.Vessel}
                direction={CarrierVisitDirection.Inbound}
                onOpenGeneralCargoReservation={onOpenGeneralCargoReservation}
                orderLocationsById={orderLocationsById}
                orderReservationsById={orderReservationsById}
              />
            )}
          </Box>

          <OrderListUploadDialogV2
            enableFieldSelectionFromOrdersView={false}
            open={orderListUploadViewStoreV2.open}
            handleDialogClose={() => {
              orderListUploadViewStoreV2.setOpen(false)
            }}
            visitType={CarrierType.Vessel}
            parseBaplie={parseBaplie}
          />
        </>
      )
    },
  ),
)
